import { useState } from "react";
import { useGetList, useRecordContext } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Plus, DollarSign } from "lucide-react";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
import { InvoiceDetail } from "./InvoiceDetail";
import { CreateInvoiceDialog } from "./CreateInvoiceDialog";
import type { BldrOsContact } from "../types";

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_type: string;
  status: string;
  title: string;
  total: number;
  paid_at: string | null;
  created_at: string;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

const TYPE_LABELS: Record<string, string> = {
  deposit: "Deposit",
  progress: "Progress",
  final: "Final",
  standard: "Invoice",
};

interface ContactInvoiceListProps {
  contactId?: string | number;
}

export function ContactInvoiceList({ contactId }: ContactInvoiceListProps) {
  const record = useRecordContext<BldrOsContact>();
  const resolvedId = contactId ?? record?.id;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isPending, total: count } = useGetList<Invoice>("invoices", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: resolvedId ? { contact_id: resolvedId } : {},
  });

  if (!resolvedId) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No contact selected.
      </div>
    );
  }

  if (selectedId) {
    return (
      <InvoiceDetail
        invoiceId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <DollarSign className="w-4 h-4" />
          Invoices
          {count !== undefined && count > 0 && (
            <span className="text-xs text-muted-foreground font-normal">
              ({count})
            </span>
          )}
        </h4>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-3 h-3" />
          New Invoice
        </button>
      </div>

      {isPending && (
        <div className="text-sm text-muted-foreground py-4">
          Loading invoices…
        </div>
      )}

      {!isPending && (!data || data.length === 0) && (
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground mb-3">
              No invoices yet.
            </p>
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 mx-auto px-3 py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Create First Invoice
            </button>
          </CardContent>
        </Card>
      )}

      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((inv) => (
            <Card
              key={inv.id}
              className="cursor-pointer hover:border-blue-200 transition-colors"
              onClick={() => setSelectedId(inv.id)}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-muted-foreground">
                        {inv.invoice_number}
                      </span>
                      <InvoiceStatusBadge status={inv.status} />
                      <span className="text-xs text-muted-foreground">
                        {TYPE_LABELS[inv.invoice_type] || inv.invoice_type}
                      </span>
                    </div>
                    <p className="text-sm font-medium truncate">
                      {inv.title || "Untitled"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(inv.created_at)}
                      {inv.paid_at && (
                        <span className="text-green-600 ml-2">
                          Paid {formatDate(inv.paid_at)}
                        </span>
                      )}
                    </p>
                  </div>
                  <p className="text-sm font-bold tabular-nums shrink-0">
                    {fmt(Number(inv.total))}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateInvoiceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => setSelectedId(id)}
        contactId={resolvedId}
      />
    </div>
  );
}
